import { Dialog, type DialogProps } from '@progress/kendo-react-dialogs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { VStack } from '~/components/elements/VStack'
import { exists } from '~/utils'

import {
  ColumnDisplayToggleForm,
  type ColumnDisplayToggleFormProps,
} from './ColumnDisplayToggleForm'
import styles from './DisplayToggleDialog.css'
import {
  ExpandTextToggleForm,
  type ExpandTextToggleFormProps,
} from './ExpandTextToggleForm'
import {
  RowDisplayToggleForm,
  type RowDisplayToggleFormProps,
} from './RowDisplayToggleForm'
import { TabSwitcher, type TabSwitcherProps } from './TabSwitcher'

export type BrandItem = {
  brandName: string
  isVisible: boolean
}

export type DisplayToggleDialogProps<
  RowId extends number | string,
  ColumnKey extends string,
> = {
  brandItems?: BrandItem[]
  columnItems?: ColumnDisplayToggleFormProps<ColumnKey>['items']
  isTextExpanded?: ExpandTextToggleFormProps['isTextExpanded']
  isVisibleExpandTextCheckbox?: boolean
  onClose?: DialogProps['onClose']
  onHiddenBrandChange?: (hiddenBrands: string[]) => void
  onHiddenColumnChange?: (hiddenColumnKeys: ColumnKey[]) => void
  onHiddenRowChange?: (hiddenIds: RowId[]) => void
  onTextExpandedChange?: ExpandTextToggleFormProps['onChange']
  rowItems?: RowDisplayToggleFormProps<RowId>['items']
}

export const DisplayToggleDialog = <
  RowId extends number | string,
  ColumnKey extends string,
>({
  brandItems,
  columnItems,
  isTextExpanded,
  isVisibleExpandTextCheckbox = false,
  onClose,
  onHiddenBrandChange,
  onHiddenColumnChange,
  onHiddenRowChange,
  onTextExpandedChange,
  rowItems,
}: DisplayToggleDialogProps<RowId, ColumnKey>) => {
  const { t } = useTranslation()

  const availableTabs = React.useMemo(
    () =>
      [
        exists(rowItems) && rowItems.length > 0 ? 'row' : null,
        exists(columnItems) && columnItems.length > 0 ? 'column' : null,
        exists(brandItems) && brandItems.length > 0 ? 'brand' : null,
      ].filter(Boolean) as TabSwitcherProps['value'][],
    [brandItems, columnItems, rowItems],
  )

  const [currentForm, setCurrentForm] = React.useState<
    TabSwitcherProps['value']
  >(availableTabs[0] ?? 'column')

  React.useEffect(() => {
    if (!availableTabs.includes(currentForm)) {
      setCurrentForm(availableTabs[0] ?? 'column')
    }
  }, [availableTabs, currentForm])

  const isVisibleSwitcher = availableTabs.length >= 2

  const handleColumnChange = React.useCallback<
    EH<ColumnDisplayToggleFormProps<ColumnKey>, 'onChange'>
  >(
    (value, columnKeys) => {
      const prevColumnKeys =
        columnItems
          ?.filter((item) => !item.isVisible)
          .map((item) => item.columnKey) ?? []

      onHiddenColumnChange?.(
        value
          ? R.differenceWith(prevColumnKeys, columnKeys, R.isDeepEqual)
          : R.concat(prevColumnKeys, columnKeys),
      )
    },
    [columnItems, onHiddenColumnChange],
  )

  const handleRowChange = React.useCallback<
    EH<RowDisplayToggleFormProps<RowId>, 'onChange'>
  >(
    (value, ids) => {
      const prevIds =
        rowItems
          ?.flatMap((item) => item.subItems)
          .filter((subItem) => !subItem.isVisible)
          .map((subItem) => subItem.id) ?? []

      onHiddenRowChange?.(
        value
          ? R.differenceWith(prevIds, ids, R.isDeepEqual)
          : R.concat(prevIds, ids),
      )
    },
    [onHiddenRowChange, rowItems],
  )

  const brandColumnItems = React.useMemo<
    ColumnDisplayToggleFormProps<string>['items']
  >(
    () =>
      brandItems?.map((item) => ({
        columnKey: item.brandName,
        isVisible: item.isVisible,
        label: item.brandName,
      })) ?? [],
    [brandItems],
  )

  const handleBrandChange = React.useCallback<
    EH<ColumnDisplayToggleFormProps<string>, 'onChange'>
  >(
    (value, brandNames) => {
      const prevHidden =
        brandItems
          ?.filter((item) => !item.isVisible)
          .map((item) => item.brandName) ?? []

      onHiddenBrandChange?.(
        value
          ? R.differenceWith(prevHidden, brandNames, R.isDeepEqual)
          : R.concat(prevHidden, brandNames),
      )
    },
    [brandItems, onHiddenBrandChange],
  )

  return (
    <Dialog
      className={styles.root}
      title={t('select_to_display_or_hide')}
      onClose={onClose}
    >
      <VStack spacing={4}>
        {isVisibleSwitcher && (
          <TabSwitcher
            showBrand={exists(brandItems) && brandItems.length > 0}
            value={currentForm}
            onChange={setCurrentForm}
          />
        )}

        {rowItems && currentForm === 'row' && (
          <VStack spacing={4}>
            {isVisibleExpandTextCheckbox && (
              <ExpandTextToggleForm
                isTextExpanded={isTextExpanded}
                onChange={onTextExpandedChange}
              />
            )}

            <RowDisplayToggleForm items={rowItems} onChange={handleRowChange} />
          </VStack>
        )}

        {columnItems && currentForm === 'column' && (
          <ColumnDisplayToggleForm
            items={columnItems}
            onChange={handleColumnChange}
          />
        )}

        {brandItems && currentForm === 'brand' && (
          <ColumnDisplayToggleForm
            items={brandColumnItems}
            onChange={handleBrandChange}
          />
        )}
      </VStack>
    </Dialog>
  )
}
